import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Paper,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  Description as DocumentIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
 
  Upload as UploadIcon,
  Download as DownloadIcon,
 
} from '@mui/icons-material';
 
import etapaTratamientoService from '../../services/etapaTratamientoService';
 
import documentoService from '../../services/documentoService';
import FileUpload from '../forms/FileUpload';
import historiaService from '../../services/historiaService';

/**
 * Componente para gestionar documentos por etapa
 * @param {Object} props - Propiedades del componente
 * @param {number} props.historiaId - ID de la historia clínica
 * @param {number} props.etapaId - ID de la etapa actual
 * @param {Object} props.etapa - Objeto con datos de la etapa (opcional)
 * @param {Array} props.documentosExistentes - Lista de documentos ya existentes (opcional)
 * @param {Function} props.onDocumentoCreado - Callback cuando se crea un documento
 * @returns {JSX.Element} Componente React
 */
const DocumentosEtapaList = ({
  historiaId,
  etapaId,
  etapa: etapaProp,
  especialidadId,
  documentosExistentes = [],
  onDocumentoCreado
}) => {
  // Estados
  const [cargando, setCargando] = useState(true);
  const [etapa, setEtapa] = useState(etapaProp || null);
  const [plantillasEtapa, setPlantillasEtapa] = useState([]);
  const [documentos, setDocumentos] = useState(documentosExistentes || []);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState(null);
  const [archivo, setArchivo] = useState(null);
  const [descripcion, setDescripcion] = useState('');
  const [estadoDocumento, setEstadoDocumento] = useState('Pendiente');
  const [requiereFirma, setRequiereFirma] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  
  const safeCallback = (callback, data) => {
  if (typeof callback === 'function') {
    try {
      callback(data);
    } catch (error) {
      console.error('Error en callback:', error);
    }
  }
};

  // Cargar datos iniciales
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setCargando(true);
        setError(null);
        
        // Si no tenemos la etapa, cargarla
        if (!etapa && etapaId) {
          const resEtapa = await etapaTratamientoService.getById(etapaId);
          setEtapa(resEtapa.data);
        }
        
        // Cargar plantillas asociadas a la etapa
        if (etapaId) {
          const resPlantillas = await etapaTratamientoService.obtenerRequerimientosEtapa(etapaId);
          setPlantillasEtapa(resPlantillas.data.plantillasDocumentos || []);
        }
        
        // Si no tenemos documentos preexistentes, cargarlos
        if (!documentosExistentes.length && historiaId && etapaId) {
          const resDocumentos = await documentoService.getByHistoria(historiaId, {
            etapa_id: etapaId
          });
          setDocumentos(resDocumentos.data);
        } else {
          setDocumentos(documentosExistentes);
        }
      } catch (error) {
        console.error('Error al cargar documentos por etapa:', error);
        setError('Error al cargar datos. Por favor, intente nuevamente.');
      } finally {
        setCargando(false);
      }
    };
    
    cargarDatos();
  }, [historiaId, etapaId, etapaProp, documentosExistentes]);
  
  // Manejar apertura del diálogo de subida
  const handleOpenUploadDialog = (plantilla = null) => {
    setPlantillaSeleccionada(plantilla);
    setDescripcion(plantilla ? plantilla.nombre : '');
    setEstadoDocumento('Pendiente');
    setRequiereFirma(false);
    setArchivo(null);
    setUploadProgress(0);
    setIsUploading(false);
    setOpenUploadDialog(true);
  };
  
  // Manejar cierre del diálogo de subida
  const handleCloseUploadDialog = () => {
    setOpenUploadDialog(false);
  };
  
  // Manejar cambio de archivo seleccionado
  const handleFileChange = (files) => {
    if (files && files.length > 0) {
      setArchivo(files[0]);
    } else {
      setArchivo(null);
    }
  };
  
  // Manejar subida del documento
  const handleUploadDocument = async () => {
  if (!archivo) {
    setError('Debe seleccionar un archivo');
    return;
  }
  
  try {
    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    // ✅ 1. Obtener datos necesarios con manejo de errores
    let pacienteId = null;
    let usuarioId = null;
    
    // Obtener paciente_id desde la historia
    if (historiaId) {
      try {
        const historiaResponse = await historiaService.getHistoria(historiaId);
        pacienteId = historiaResponse?.data?.paciente_id;
       } catch (err) {
        console.warn('⚠️ No se pudo obtener paciente_id desde historia:', err);
      }
    }
    
    // Obtener usuario_id del localStorage
    try {
      const userString = localStorage.getItem('user');
      if (userString) {
        const userObj = JSON.parse(userString);
        usuarioId = userObj.id;
       }
    } catch (err) {
      console.warn('⚠️ No se pudo obtener usuario_id:', err);
    }
    
    // ✅ 2. Valores por defecto para desarrollo
    if (!pacienteId) {
      console.warn('⚠️ Usando paciente_id por defecto');
      pacienteId = 1;
    }
    
    if (!usuarioId) {
      console.warn('⚠️ Usando usuario_id por defecto');
      usuarioId = 1;
    }

    // ✅ 3. Crear FormData con campos correctos según el backend
    const formData = new FormData();
    
    // Campos obligatorios del modelo
    formData.append('file', archivo);
    formData.append('paciente_id', pacienteId);
    formData.append('historia_clinica_id', historiaId);
    formData.append('subido_por', usuarioId);
    
    // Campos opcionales
    if (especialidadId) {
      formData.append('especialidad_id', especialidadId);
    }
    if (etapaId) {
      formData.append('etapa_id', etapaId);
    }
    formData.append('descripcion', descripcion || archivo.name);
    formData.append('estado', estadoDocumento || 'Pendiente');
    formData.append('requiere_firma', requiereFirma ? 'true' : 'false');
    
    if (plantillaSeleccionada?.id) {
      formData.append('plantilla_id', plantillaSeleccionada.id);
    }

    // Debug: Mostrar contenido del FormData
     for (let [key, value] of formData.entries()) {
     }

    // ✅ 4. Configurar progreso de subida
    const config = {
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
         }
      }
    };

    // ✅ 5. Subir documento usando el servicio corregido
     const response = await documentoService.subirDocumento(formData, config);
    
 
    // ✅ 6. Manejar respuesta exitosa
    if (response && response.success && response.data) {
      // Actualizar lista local de documentos
      const nuevoDocumento = response.data;
      setDocumentos(prev => [nuevoDocumento, ...prev]);
      
      // Notificar al componente padre
      if (typeof onDocumentoCreado === 'function') {
         onDocumentoCreado(nuevoDocumento);
      }
      
      // Cerrar diálogo y mostrar éxito
      handleCloseUploadDialog();
      
      // Mostrar mensaje de éxito (opcional)
       
    } else {
      throw new Error('Respuesta inválida del servidor');
    }
    
  } catch (error) {
    console.error('❌ Error detallado al subir documento:', error);
    
    // Mostrar mensaje de error específico
    let errorMsg = 'Error al subir el documento. Por favor, intente nuevamente.';
    
    if (error.message) {
      errorMsg = error.message;
    }
    
    setError(errorMsg);
    
    // Resetear progreso en caso de error
    setUploadProgress(0);
    
  } finally {
    setIsUploading(false);
  }
};
  
  // Verificar si un documento está completo para una plantilla
  const isDocumentoCompleto = (plantillaId) => {
    return documentos.some(doc => 
      doc.plantilla_id === plantillaId && 
      ['Completado', 'Revisado'].includes(doc.estado)
    );
  };
  
  // Obtener documento para una plantilla
  const getDocumentoPlantilla = (plantillaId) => {
    return documentos.find(doc => doc.plantilla_id === plantillaId);
  };
  
  // Manejar descarga de documento
  const handleDownloadDocument = async (documentoId) => {
    try {
      await documentoService.download(documentoId);
    } catch (error) {
      console.error('Error al descargar documento:', error);
      setError('Error al descargar el documento. Por favor, intente nuevamente.');
    }
  };
  
  // Manejar eliminación de documento
  const handleDeleteDocument = async (documentoId) => {
    try {
      await documentoService.delete(documentoId);
      
      // Actualizar la lista de documentos
      setDocumentos(prev => prev.filter(doc => doc.id !== documentoId));
    } catch (error) {
      console.error('Error al eliminar documento:', error);
      setError('Error al eliminar el documento. Por favor, intente nuevamente.');
    }
  };
  
  // Renderizar plantillas requeridas
  const renderPlantillasRequeridas = () => {
    if (plantillasEtapa.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" align="center">
          No hay plantillas requeridas para esta etapa.
        </Typography>
      );
    }
    
    return (
      <List>
        {plantillasEtapa
          .sort((a, b) => a.orden_en_etapa - b.orden_en_etapa)
          .map(plantilla => {
            const documento = getDocumentoPlantilla(plantilla.id);
            const completado = isDocumentoCompleto(plantilla.id);
            
            return (
              <ListItem key={plantilla.id}>
                <ListItemIcon>
                  <DocumentIcon color={completado ? 'success' : 'action'} />
                </ListItemIcon>
                
                <ListItemText
                  primary={plantilla.nombre}
                  secondary={
                    <>
                      {plantilla.descripcion}
                      {documento && (
                        <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                          Subido: {new Date(documento.fecha_subida).toLocaleDateString()}
                        </Box>
                      )}
                    </>
                  }
                />
                
                <ListItemSecondaryAction>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {documento ? (
                      <>
                        <Chip
                          size="small"
                          label={documento.estado}
                          color={
                            documento.estado === 'Completado' ? 'success' :
                            documento.estado === 'Revisado' ? 'primary' :
                            'default'
                          }
                          sx={{ mr: 1 }}
                        />
                        
                        <Tooltip title="Descargar">
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadDocument(documento.id)}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteDocument(documento.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<UploadIcon />}
                        onClick={() => handleOpenUploadDialog(plantilla)}
                      >
                        Subir
                      </Button>
                    )}
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
      </List>
    );
  };
  
  // Renderizar otros documentos
  const renderOtrosDocumentos = () => {
    const otrosDocumentos = documentos.filter(doc => 
      !doc.plantilla_id || !plantillasEtapa.some(p => p.id === doc.plantilla_id)
    );
    
    if (otrosDocumentos.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" align="center">
          No hay documentos adicionales en esta etapa.
        </Typography>
      );
    }
    
    return (
      <List>
        {otrosDocumentos.map(documento => (
          <ListItem key={documento.id}>
            <ListItemIcon>
              <DocumentIcon />
            </ListItemIcon>
            
            <ListItemText
              primary={documento.nombre}
              secondary={
                <>
                  {documento.descripcion}
                  <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                    Subido: {new Date(documento.fecha_subida).toLocaleDateString()}
                  </Box>
                </>
              }
            />
            
            <ListItemSecondaryAction>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip
                  size="small"
                  label={documento.estado}
                  color={
                    documento.estado === 'Completado' ? 'success' :
                    documento.estado === 'Revisado' ? 'primary' :
                    'default'
                  }
                  sx={{ mr: 1 }}
                />
                
                <Tooltip title="Descargar">
                  <IconButton
                    size="small"
                    onClick={() => handleDownloadDocument(documento.id)}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Eliminar">
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteDocument(documento.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    );
  };
  
  // Si está cargando, mostrar indicador de progreso
  if (cargando) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        {/* Plantillas requeridas */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Documentos Requeridos en la Etapa"
              subheader={etapa?.nombre ? `Documentos necesarios para la etapa "${etapa.nombre}"` : ''}
              action={
                <Tooltip title="Subir otro documento">
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenUploadDialog(null)}
                    size="small"
                  >
                    Subir otro documento
                  </Button>
                </Tooltip>
              }
            />
            <Divider />
            <CardContent>
              {renderPlantillasRequeridas()}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Otros documentos */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Otros Documentos"
              subheader="Documentos adicionales subidos en esta etapa"
            />
            <Divider />
            <CardContent>
              {renderOtrosDocumentos()}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Diálogo para subir documento */}
      <Dialog
        open={openUploadDialog}
        onClose={!isUploading ? handleCloseUploadDialog : undefined}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {plantillaSeleccionada 
            ? `Subir documento: ${plantillaSeleccionada.nombre}` 
            : 'Subir documento adicional'}
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                required
                disabled={isUploading}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Estado del documento</InputLabel>
                <Select
                  value={estadoDocumento}
                  onChange={(e) => setEstadoDocumento(e.target.value)}
                  label="Estado del documento"
                  disabled={isUploading}
                >
                  <MenuItem value="Pendiente">Pendiente</MenuItem>
                  <MenuItem value="Completado">Completado</MenuItem>
                  <MenuItem value="Revisado">Revisado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={requiereFirma}
                    onChange={(e) => setRequiereFirma(e.target.checked)}
                    disabled={isUploading}
                  />
                }
                label="Requiere firma digital"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Seleccionar archivo
                </Typography>
                
                <FileUpload
                  onFileSelected={handleFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  maxFiles={1}
                  maxSize={10 * 1024 * 1024} // 10 MB
                  disabled={isUploading}
                />
              </Paper>
            </Grid>
            
            {isUploading && (
              <Grid item xs={12}>
                <Box sx={{ width: '100%', mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Subiendo documento: {uploadProgress}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={uploadProgress} 
                  />
                </Box>
              </Grid>
            )}
            
            {error && (
              <Grid item xs={12}>
                <Alert severity="error">
                  {error}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleCloseUploadDialog}
            disabled={isUploading}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleUploadDocument}
            variant="contained"
            disabled={!archivo || isUploading}
            startIcon={isUploading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {isUploading ? 'Subiendo...' : 'Subir documento'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentosEtapaList;